// Home controller (ARCHITECTURE §5.2, §6.1 C3). Today's pages/Home.tsx data
// logic: the same two loads in the same order with the same deps, so the
// request sequence is unchanged. The homepage goes through usePageResource
// (#1291: latest request wins, friendly error).
// - The homepage fetch refetches when the fan switches sport on a multi-sport
//   tenant; single-sport tenants omit ?sport so caching keys stay shared.
// - The featured-gallery fetch runs once per mount and is not refetched on a
//   sport switch (a known quirk, kept).
// The view-only effects (scroll fades, the Teams auto-scroll, horizontal
// scrollers) stay in the template's view.
// #583: with 2+ live divisions in the sport the homepage request carries the
// pick (`?division=<id>`, or `all`: the union of every division's upcoming
// games and results, tagged). Below that, the request is exactly today's.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '@/services/api'
import type { GalleryImage } from '@/services/api/types'
import { useLeague } from '@/contexts/LeagueContext'
import { addonEntitled, registrationSiteOn } from '@/contexts/tenantAccess'
import { memberApi, type MemberRegistrationList } from '@/services/memberApi'
import { useSport, useSportContext } from '@/contexts/SportContext'
import { divisionArg, divisionRef, useDivision } from '../../division'
import type { HomeDivisionMode } from '../../template/types'
import { deriveHome, deriveOpenRegistrations } from './derive'
import { usePageResource } from '../usePageResource'
import type { HomePageVM, HomeSectionType } from './types'

export interface HomeOptions {
  /**
   * #583: 'follow' (default) uses the fan's division pick; 'all' always shows
   * All divisions (a template with no Home switcher). No effect below 2 live
   * divisions: the request is today's either way.
   */
  division?: HomeDivisionMode
}

export function useHomePage(titles?: Record<HomeSectionType, string>, opts: HomeOptions = {}): HomePageVM {
  const { league } = useLeague()
  const { activeSportId, packs } = useSportContext()
  const sport = useSport()
  // Pass sport filter only when the tenant runs >1 sport. Single-sport tenants
  // omit the param so caching keys stay shared and behaviour matches today.
  const tenantSports = new Set((league?.seasons ?? []).map((s) => s.sport_id))
  const sportFilter = tenantSports.size > 1 ? activeSportId : null
  const picked = useDivision()
  // 'all': the page ignores the pick (the switcher stays usable elsewhere, so
  // the VM's copy is hidden and reads All).
  const forceAll = opts.division === 'all' && picked.enabled
  const division = forceAll ? { ...picked, visible: false, value: 'all' as const, active: null } : picked
  const divArg = divisionArg(division)

  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([])

  // Fetch homepage data. Re-fetches when the fan switches sports on a
  // multi-sport tenant — the backend swaps activeSeason + featured +
  // upcoming + recent to that sport, so the whole homepage reloads.
  //
  // #388: a response for a sport (or an unscoped request from before the
  // tenant loaded) that has since been superseded is dropped, so a late
  // unscoped / other-sport payload can't overwrite the active sport's.
  const res = usePageResource(() => api.getHomepage({ sport: sportFilter, division: divArg }), [sportFilter, divArg])
  const { data, loading, error, dismissError } = res
  // retry() refetches the homepage and re-reads the registration spotlight.
  const pageRetry = res.retry
  const [attempt, setAttempt] = useState(0)
  const retry = useCallback(() => {
    pageRetry()
    setAttempt((n) => n + 1)
  }, [pageRetry])

  // Fetch gallery images for homepage widget
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await api.getGalleryHomepageFeatured()
        setGalleryImages(response.images)
      } catch (err) {
        console.error('Error fetching gallery images:', err)
      }
    }
    fetchGallery()
  }, [])

  // The registration spotlight (Website › Design › Registration's "Home
  // page spotlight", October 28): the member Register list, only with the
  // strict registration add-on and the switch on. A failed read shows none.
  // retry() re-reads it too (`attempt`), as it refetches the homepage.
  const spotlightOn = addonEntitled(league, 'registration') && registrationSiteOn(league, 'home_spotlight')
  const [registrations, setRegistrations] = useState<MemberRegistrationList | null>(null)
  useEffect(() => {
    if (!spotlightOn) {
      setRegistrations(null)
      return
    }
    let stale = false
    memberApi.registrations()
      .then((r) => { if (!stale) setRegistrations(r) })
      .catch(() => { if (!stale) setRegistrations(null) })
    return () => { stale = true }
  }, [spotlightOn, attempt])
  const openForms = useMemo(
    () => (spotlightOn && registrations
      ? deriveOpenRegistrations(registrations.registrations, registrations.timezone ?? null, sportFilter)
      : []),
    [spotlightOn, registrations, sportFilter],
  )

  // Featured standings resolve their columns per season like today's
  // StandingsTable (useSportForSeason): the season's sport, else the default.
  const packForSeason = useCallback((seasonId: number | null) => {
    const season = seasonId != null ? league?.seasons?.find((s) => s.id === seasonId) : null
    const id = season?.sport_id || league?.default_sport_id || 'basketball'
    return packs[id] ?? sport
  }, [league, packs, sport])

  const live = division.live
  const divisionTag = useMemo(
    () => (division.enabled ? (id: number | null | undefined) => divisionRef(live, id) : undefined),
    [division.enabled, live],
  )
  const vm = useMemo(
    () => deriveHome({ league, sport, packForSeason, data, galleryImages, loading, error, titles, openForms, divisionTag }),
    [league, sport, packForSeason, data, galleryImages, loading, error, titles, openForms, divisionTag],
  )
  return { ...vm, retry, dismissError, division }
}
